import { Injectable } from '@angular/core';
import { FirebaseTSFirestore } from 'firebasets/firebasetsFirestore/firebaseTSFirestore';
import { FirebaseTSAuth } from 'firebasets/firebasetsAuth/firebaseTSAuth';
import { BehaviorSubject, Observable } from 'rxjs';

export interface UserSettings {
  personalInfo: {
    username: string;
    email: string;
    birthdate: string;
    gender: string;
  };
  privacy: {
    profileVisibility: 'public' | 'private' | 'friends';
    showOnlineStatus: boolean;
    allowMessages: 'everyone' | 'friends' | 'none';
  };
  contentDisplay: {
    theme: 'light' | 'dark' | 'system';
    language: string;
    autoplayVideos: boolean;
    showStories: boolean;
  };
}

@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  private firestore = new FirebaseTSFirestore();
  private auth = new FirebaseTSAuth();
  
  private settingsSubject = new BehaviorSubject<UserSettings | null>(null);
  settings$ = this.settingsSubject.asObservable();

  private defaultSettings: UserSettings = {
    personalInfo: {
      username: '',
      email: '',
      birthdate: '',
      gender: ''
    },
    privacy: {
      profileVisibility: 'public',
      showOnlineStatus: true,
      allowMessages: 'everyone'
    },
    contentDisplay: {
      theme: 'system',
      language: 'fr',
      autoplayVideos: true,
      showStories: true
    }
  };

  constructor() {
    this.initializeSettings();
  }

  private async initializeSettings() {
    const userId = this.auth.getAuth().currentUser?.uid;
    if (!userId) return;

    this.firestore.getDocument({
      path: ["Users", userId],
      onComplete: (result) => {
        const userData = result.data();
        const settings: UserSettings = {
          personalInfo: {
            username: userData?.username || this.defaultSettings.personalInfo.username,
            email: userData?.email || this.defaultSettings.personalInfo.email,
            birthdate: userData?.birthdate || this.defaultSettings.personalInfo.birthdate,
            gender: userData?.gender || this.defaultSettings.personalInfo.gender
          },
          privacy: {
            profileVisibility: userData?.privacy?.profileVisibility || this.defaultSettings.privacy.profileVisibility,
            showOnlineStatus: userData?.privacy?.showOnlineStatus ?? this.defaultSettings.privacy.showOnlineStatus,
            allowMessages: userData?.privacy?.allowMessages || this.defaultSettings.privacy.allowMessages
          },
          contentDisplay: {
            theme: userData?.contentDisplay?.theme || this.defaultSettings.contentDisplay.theme,
            language: userData?.contentDisplay?.language || this.defaultSettings.contentDisplay.language,
            autoplayVideos: userData?.contentDisplay?.autoplayVideos ?? this.defaultSettings.contentDisplay.autoplayVideos,
            showStories: userData?.contentDisplay?.showStories ?? this.defaultSettings.contentDisplay.showStories
          }
        };
        this.settingsSubject.next(settings);
      },
      onFail: (error) => {
        console.error('Error loading settings:', error);
        this.settingsSubject.next(this.defaultSettings);
      }
    });
  }

  updateSettings(section: keyof UserSettings, updates: Partial<UserSettings[keyof UserSettings]>) {
    const userId = this.auth.getAuth().currentUser?.uid;
    if (!userId) return;

    const currentSettings = this.settingsSubject.value;
    if (!currentSettings) return;

    // Mettre à jour localement
    const updatedSettings = {
      ...currentSettings,
      [section]: {
        ...currentSettings[section],
        ...updates
      }
    };

    // Mettre à jour dans Firestore
    this.firestore.update({
      path: ["Users", userId],
      data: {
        [section]: updatedSettings[section]
      },
      onComplete: () => {
        console.log(`${section} settings updated successfully`);
        this.settingsSubject.next(updatedSettings);
      },
      onFail: (error) => {
        console.error(`Error updating ${section} settings:`, error);
      }
    });
  }

  getSettings(): Observable<UserSettings | null> {
    return this.settings$;
  }

  reloadSettings() {
    this.initializeSettings();
  }
}
